import type { RoundingRule } from './types';

/** Half-up rounding to an integer (centavos). Tolerates float noise like 2.4999999999. */
export function roundHalfUp(value: number): number {
  return Math.floor(value + 0.5 + 1e-9);
}

export function bpsOf(value: number, bps: number): number {
  return (value * bps) / 10_000;
}

/**
 * Splits `total` across `parts` (raw values) so the integer shares sum exactly to `total`
 * (largest-remainder method). Used for sub-rows such as Depreciação/Energia/Manutenção.
 */
export function allocate(total: number, parts: number[]): number[] {
  const floors = parts.map((p) => Math.floor(p));
  let left = total - floors.reduce((a, b) => a + b, 0);
  const order = parts
    .map((p, i) => ({ i, rem: p - Math.floor(p) }))
    .toSorted((a, b) => b.rem - a.rem || a.i - b.i);
  const out = [...floors];
  for (const { i } of order) {
    if (left <= 0) break;
    out[i] = (out[i] ?? 0) + 1;
    left -= 1;
  }
  // If raw parts summed above `total` (rounding down overall), take back from the smallest remainders.
  for (const { i } of order.toReversed()) {
    if (left >= 0) break;
    if ((out[i] ?? 0) > 0) {
      out[i] = (out[i] ?? 0) - 1;
      left += 1;
    }
  }
  return out;
}

function ending(rule: RoundingRule): number | null {
  if (rule === 'end_90') return 90;
  if (rule === 'end_99') return 99;
  return null;
}

/** Friendly price at or above `price` ("Sempre arredonda para cima"). */
export function roundFriendlyUp(price: number, rule: RoundingRule): number {
  const p = Math.ceil(price - 1e-9);
  if (rule === 'none') return p;
  if (rule === 'whole') return Math.ceil(p / 100) * 100;
  const end = ending(rule) ?? 0;
  let candidate = Math.floor(p / 100) * 100 + end;
  if (candidate < p) candidate += 100;
  return candidate;
}

/** Largest friendly price strictly below `limit` (e.g. 78,90 below 79,00). */
export function friendlyBelow(limit: number, rule: RoundingRule): number {
  const max = limit - 1;
  if (rule === 'none') return max;
  if (rule === 'whole') return Math.floor(max / 100) * 100;
  const end = ending(rule) ?? 0;
  let candidate = Math.floor(max / 100) * 100 + end;
  if (candidate > max) candidate -= 100;
  return candidate;
}
